import { PropsWithChildren, useState } from 'react';
import { DialogContent, TextField, Typography, Box, Checkbox, FormControlLabel, Divider } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { GroupView } from '@views';
import { ToDate } from '@utils/DateUtils';
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { SurveyStatus } from '@app/models/Survey';
import {
  QuestionnaireAssignment,
  QuestionnaireFormOption,
  QuestionnaireScheduleOption,
} from '@app/api/QuestionnaireAssignmentStore';
import { DateTime } from 'luxon';
import { changeQuestionnaireSchedule, defaultScheduleId, toggleQuestionnaire } from './questionnaireAssignments';
import { LockedOptionsSection } from './LockedOptionsSection';

interface Props extends PropsWithChildren {
  group: GroupView;
  onGroupChange: (e: any) => void;
  surveys: SurveyStatus[];
  selectedSurveyIds: number[];
  onSurveyIdsChange: (ids: number[]) => void;
  questionnaires: QuestionnaireFormOption[];
  schedules: QuestionnaireScheduleOption[];
  selectedAssignments: QuestionnaireAssignment[];
  onAssignmentsChange: (assignments: QuestionnaireAssignment[]) => void;
}

export const GroupDialogContent: React.FC<Props> = ({
  children,
  group,
  onGroupChange,
  surveys,
  selectedSurveyIds,
  onSurveyIdsChange,
  questionnaires,
  schedules,
  selectedAssignments,
  onAssignmentsChange,
}) => {
  const [roleNameError, setRoleNameError] = useState(false);
  const [roleNameHelperText, setRoleNameHelperText] = useState('');
  const validateRoleName = () => {
    if (group.name.length < 1) {
      setRoleNameError(true);
      setRoleNameHelperText('Role name is required');
    } else {
      setRoleNameError(false);
      setRoleNameHelperText('');
    }
  };

  return (
    <DialogContent
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        '&.MuiDialogContent-root': {
          paddingTop: 3,
        },
      }}
    >
      <TextField
        label={'Group ID'}
        value={group.id}
        disabled={true}
        slotProps={{
          input: {
            readOnly: true,
            hidden: true,
          },
        }}
        fullWidth={true}
        rows={1}
        helperText={
          group.createdAt
            ? `Created on ${DateTime.fromISO(group.createdAt).toFormat('MM/dd/yyyy hh:mm a')}${group.createdBy ? ` by ${group.createdBy}` : ''}`
            : undefined
        }
      >
        {group.id}
      </TextField>
      <TextField
        required
        id={'name'}
        onChange={onGroupChange}
        value={group.name}
        label={'Group Name'}
        error={roleNameError}
        onKeyUp={validateRoleName}
        fullWidth={true}
        variant={'outlined'}
        helperText={roleNameHelperText}
      ></TextField>
      <DatePicker
        label={'Start Date'}
        key={'startDate'}
        value={ToDate(group.startDate)}
        onChange={onGroupChange}
      ></DatePicker>
      <TextField
        label={'End Date'}
        value={group.endDate || ''}
        disabled={true}
        slotProps={{
          input: {
            readOnly: true,
          },
        }}
        fullWidth={true}
        helperText={'Auto-calculated: Sunday of week 12'}
      />
      <TextField
        id={'shortDescription'}
        onChange={onGroupChange}
        value={group.shortDescription || ''}
        label={'Short Description'}
        multiline
        fullWidth={true}
        rows={4}
        variant={'outlined'}
        content={group.shortDescription}
      ></TextField>
      <TextField
        id={'userCount'}
        onChange={onGroupChange}
        value={group.userCount}
        label={'Expected Number of Participants'}
        type={'number'}
        fullWidth={true}
        rows={4}
        variant={'outlined'}
      ></TextField>
      <FormControl fullWidth variant="outlined">
        <InputLabel id="inPersonCelebration-label">
          Should cohort celebration survey include an in-person option?
        </InputLabel>
        <Select
          labelId="inPersonCelebration-label"
          id="inPersonCelebration"
          name="inPersonCelebration"
          label="Should cohort celebration survey include an in-person option?"
          value={group.inPersonCelebration ? 'yes' : 'no'}
          onChange={onGroupChange}
          disabled={group.currentWeekNumberIndexZero >= 11}
        >
          <MenuItem value="no">No</MenuItem>
          <MenuItem value="yes">Yes</MenuItem>
        </Select>
      </FormControl>
      <Divider sx={{ mt: 2 }} />
      <Typography variant="h6" sx={{ mt: 2 }}>
        Team Milestones
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pl: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2">Team Status:</Typography>
          <Typography
            variant="subtitle2"
            sx={{ color: group.finalized ? 'error.main' : 'success.main', fontWeight: 'bold' }}
          >
            {group.finalized ? 'Closed' : 'Open'}
          </Typography>
        </Box>
        {group.finalizedAt ? (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            Closed on {DateTime.fromISO(group.finalizedAt).toFormat('MM/dd/yyyy hh:mm a')} by{' '}
            {group.finalizedByUserName}
          </Typography>
        ) : (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            Use &quot;View Report Card &amp; Close Group&quot; from the Actions menu to close.
          </Typography>
        )}
      </Box>
      {surveys && surveys.length > 0 && (
        <LockedOptionsSection title="Survey Assignment">
          {(locked) => (
            <Box>
              {surveys.map((survey) => (
                <FormControlLabel
                  key={survey.id}
                  disabled={locked}
                  control={
                    <Checkbox
                      checked={selectedSurveyIds?.includes(survey.id) || false}
                      onChange={() =>
                        onSurveyIdsChange(
                          selectedSurveyIds.includes(survey.id)
                            ? selectedSurveyIds.filter((id) => id !== survey.id)
                            : [...selectedSurveyIds, survey.id]
                        )
                      }
                    />
                  }
                  label={survey.title}
                />
              ))}
            </Box>
          )}
        </LockedOptionsSection>
      )}
      {questionnaires.length > 0 && (
        <LockedOptionsSection title="Questionnaire Assignment">
          {(locked) => (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {questionnaires.map((questionnaire) => {
                const assignment = selectedAssignments.find((selected) => selected.formId === questionnaire.id);
                return (
                  <Box key={questionnaire.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <FormControlLabel
                      disabled={locked}
                      control={
                        <Checkbox
                          checked={!!assignment}
                          disabled={locked || (!assignment && schedules.length === 0)}
                          onChange={() =>
                            onAssignmentsChange(
                              toggleQuestionnaire(selectedAssignments, questionnaire.id, defaultScheduleId(schedules))
                            )
                          }
                        />
                      }
                      label={questionnaire.name}
                    />
                    {assignment && (
                      <FormControl size="small" sx={{ minWidth: 240 }} disabled={locked}>
                        <InputLabel id={`questionnaire-schedule-${questionnaire.id}-label`}>Schedule</InputLabel>
                        <Select
                          labelId={`questionnaire-schedule-${questionnaire.id}-label`}
                          label="Schedule"
                          value={assignment.scheduleId}
                          onChange={(e) =>
                            onAssignmentsChange(
                              changeQuestionnaireSchedule(selectedAssignments, questionnaire.id, Number(e.target.value))
                            )
                          }
                        >
                          {schedules.map((schedule) => (
                            <MenuItem key={schedule.id} value={schedule.id}>
                              {schedule.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </LockedOptionsSection>
      )}
      {children}
    </DialogContent>
  );
};
